import { useState } from "react";
import type { CategoryType } from "@/types";

interface FilterBarProps {
  type: CategoryType;
  keyword: string;
  sort: string;
  total: number;
  keywordPlaceholder: string;
  onKeywordChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onReset: () => void;
}

export default function CategoryFilterBar({
  type,
  keyword,
  sort,
  total,
  keywordPlaceholder,
  onKeywordChange,
  onSortChange,
  onReset,
}: FilterBarProps) {
  const [expanded, setExpanded] = useState(false);
  const showSort = type === "synopsis" || type === "updates";

  const sortOptions =
    type === "updates"
      ? [
          { value: "desc", label: "最新更新在前" },
          { value: "asc", label: "最早播出在前" },
        ]
      : [
          { value: "asc", label: "按集数顺序" },
          { value: "desc", label: "按集数倒序" },
        ];

  const controls = (
    <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end lg:items-center">
      <div className="flex w-full flex-col gap-1.5 sm:w-auto">
        <label className="font-label text-xs text-foreground-500">
          {type === "cast" ? "按演员姓名 / 角色筛选" : "按关键词筛选"}
        </label>
        <div className="flex w-full items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 sm:w-72">
          <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base leading-none"></i>
          </span>
          <input
            value={keyword}
            onChange={(event) => onKeywordChange(event.target.value)}
            type="text"
            placeholder={keywordPlaceholder}
            className="w-full bg-transparent font-label text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
          />
          {keyword && (
            <button
              type="button"
              aria-label="清空关键词"
              onClick={() => onKeywordChange("")}
              className="flex h-4 w-4 items-center justify-center text-foreground-400 hover:text-foreground-700"
            >
              <i className="ri-close-circle-fill text-sm leading-none"></i>
            </button>
          )}
        </div>
      </div>

      {showSort && (
        <div className="flex w-full flex-col gap-1.5 sm:w-auto">
          <label className="font-label text-xs text-foreground-500">排序方式</label>
          <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
            {sortOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onSortChange(option.value)}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 font-label text-xs transition-colors ${
                  sort === option.value
                    ? "bg-primary-700 text-background-50"
                    : "text-foreground-600 hover:bg-background-100"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 sm:ml-auto">
        <span className="font-label text-xs text-foreground-500">
          共 <span className="font-semibold text-foreground-800">{total}</span> 条结果
        </span>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 font-label text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
        >
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-refresh-line text-sm leading-none"></i>
          </span>
          重置筛选
        </button>
      </div>
    </div>
  );

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-center justify-between lg:hidden"
      >
        <span className="flex items-center gap-2 font-label text-sm font-medium text-foreground-800">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-filter-3-line text-base leading-none"></i>
          </span>
          筛选与排序
        </span>
        <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
          <i className={expanded ? "ri-arrow-up-s-line text-base leading-none" : "ri-arrow-down-s-line text-base leading-none"}></i>
        </span>
      </button>
      <div className={`${expanded ? "mt-4 block" : "hidden"} lg:mt-0 lg:block`}>{controls}</div>
    </div>
  );
}